import {
  CANONICAL_UNIT,
  CATEGORIES,
  CATEGORY_DEFAULTS,
  type Category,
  COUNTED_WAYS,
  DISPLAY_UNITS,
  isDecimalString,
  PACK_TERMS,
  sameSize,
  type UnitId,
} from "@bl/domain";
import { Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { type Packaging, type PackagingEntry, send, useChange, usePackaging } from "../api";
import { Dialog, ErrorNote, Field } from "../components/ui";
import { unitLabel } from "../format";
import { useNotify } from "../notify";

/**
 * Settings > Packaging (Tyler, 2026-10-04, from Scott's Jam): what each kind of item comes in, and
 * the sizes it is received in. Packs are set once and left alone, so they are kept here, kind by kind,
 * rather than on each item. It starts from the built-in list; a brewery renames a size (Five Star
 * PBW's 50 lb bag is a bucket), adds its own (Oregon Fruit's 11 lb bag beside the 42 lb one), or
 * takes one off. Invoices are read against it.
 *
 * Opened from an invoice's pack line too, on that line's kind and size, so a pack called the wrong
 * thing is put right where it is noticed, without leaving the review.
 */
export function PackagingDialog({
  open,
  onClose,
  category,
  size,
}: {
  open: boolean;
  onClose: () => void;
  /** The kind to open on; the first otherwise. */
  category?: Category | null;
  /** A size to open renaming, canonical. */
  size?: string;
}) {
  return (
    <Dialog open={open} onClose={onClose} title="Packaging" wide>
      {open ? <PackagingBody start={category ?? null} focus={size} onDone={onClose} /> : null}
    </Dialog>
  );
}

function PackagingBody({
  start,
  focus,
  onDone,
}: {
  start: Category | null;
  focus?: string;
  onDone: () => void;
}) {
  const { data: kinds = [], isLoading, error } = usePackaging();
  const [category, setCategory] = useState<Category>(start ?? CATEGORIES[0]);
  const kind = kinds.find((k) => k.category === category);
  return (
    <>
      <div className="dialog-body">
        <p className="muted" style={{ margin: 0 }}>
          What each kind of item comes in. An invoice's printed size is read against these: within
          1% of one, it is that pack. Renaming a pack renames the stock already counted in it; no
          amount changes.
        </p>
        <Field label="Kind">
          <select
            className="select"
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_DEFAULTS[c].label}
              </option>
            ))}
          </select>
        </Field>
        <ErrorNote error={error} />
        {isLoading ? <p className="muted">Loading</p> : null}
        {kind ? (
          <KindPacks key={category} kind={kind} focus={start === category ? focus : undefined} />
        ) : null}
        <datalist id="pack-terms">
          {PACK_TERMS.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
      </div>
      <div className="dialog-footer">
        <button type="button" className="button primary" onClick={onDone}>
          Done
        </button>
      </div>
    </>
  );
}

const entryKey = (p: PackagingEntry) => `${p.dimension}|${p.size}`;

function KindPacks({ kind, focus }: { kind: Packaging; focus?: string }) {
  const label = CATEGORY_DEFAULTS[kind.category].label;
  return (
    <div className="stack">
      {kind.packs.length ? (
        <div className="list-rows packaging-rows">
          {kind.packs.map((p) => (
            <PackRow
              key={entryKey(p)}
              category={kind.category}
              entry={p}
              startRenaming={focus !== undefined && sameSize(focus, p.size)}
            />
          ))}
        </div>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          {label} comes in no packs yet; it is counted by amount alone.
        </p>
      )}
      {kind.removed.length ? (
        <div className="stack" style={{ gap: 6 }}>
          <span className="muted">Taken off {label.toLowerCase()}</span>
          {kind.removed.map((p) => (
            <RemovedRow key={entryKey(p)} category={kind.category} entry={p} />
          ))}
        </div>
      ) : null}
      <AddPack category={kind.category} />
    </div>
  );
}

/** A size as the API takes it: the canonical figure in the canonical unit. */
const sizeOf = (p: PackagingEntry) => ({ value: p.size, unit: CANONICAL_UNIT[p.dimension] });

function useSaved() {
  const notify = useNotify();
  return (result: { packaging: Packaging; renamed: number }) =>
    notify(
      result.renamed
        ? `Packaging saved. ${result.renamed} ${result.renamed === 1 ? "item" : "items"} renamed`
        : "Packaging saved",
    );
}

function PackRow({
  category,
  entry,
  startRenaming,
}: {
  category: Category;
  entry: PackagingEntry;
  startRenaming: boolean;
}) {
  const saved = useSaved();
  const [renaming, setRenaming] = useState(startRenaming);
  const [name, setName] = useState(entry.name);
  const rename = useChange(send.setPackaging, {
    onSuccess: (r) => {
      saved(r);
      setRenaming(false);
    },
  });
  const remove = useChange(send.removePackaging, { onSuccess: saved });
  const restore = useChange(send.restorePackaging, { onSuccess: saved });
  const note =
    entry.source === "renamed"
      ? `Built in as ${entry.listName}`
      : entry.source === "own"
        ? "Added here"
        : "Built in";

  return (
    <div>
      {renaming ? (
        <form
          className="row packaging-rename"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) rename.mutate({ category, name: name.trim(), size: sizeOf(entry) });
          }}
        >
          <span>{entry.says}</span>
          <input
            className="input"
            aria-label={`What a ${entry.says} pack is called`}
            list="pack-terms"
            value={name}
            // biome-ignore lint/a11y/noAutofocus: opened to rename this size, from an invoice line
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
          <span className="spacer" />
          <button
            type="button"
            className="button small"
            onClick={() => {
              setName(entry.name);
              setRenaming(false);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="button small primary"
            disabled={rename.isPending || !name.trim()}
          >
            Save
          </button>
        </form>
      ) : (
        <div className="row packaging-row">
          <span className="row packaging-what">
            <strong>{entry.label}</strong>
            <span className="muted">{note}</span>
          </span>
          <span className="spacer" />
          <span className="row packaging-actions">
            <button
              type="button"
              className="link-button row"
              style={{ gap: 4 }}
              onClick={() => setRenaming(true)}
            >
              <Pencil size={14} /> Rename
            </button>
            {entry.source === "renamed" ? (
              <button
                type="button"
                className="link-button row"
                style={{ gap: 4 }}
                disabled={restore.isPending}
                onClick={() => restore.mutate({ category, size: sizeOf(entry) })}
              >
                <RotateCcw size={14} /> Put back
              </button>
            ) : null}
            <button
              type="button"
              className="link-button row danger-link"
              style={{ gap: 4 }}
              disabled={remove.isPending}
              onClick={() => remove.mutate({ category, size: sizeOf(entry) })}
            >
              <Trash2 size={14} /> Remove
            </button>
          </span>
        </div>
      )}
      <ErrorNote error={rename.error ?? remove.error ?? restore.error} />
    </div>
  );
}

function RemovedRow({ category, entry }: { category: Category; entry: PackagingEntry }) {
  const saved = useSaved();
  const restore = useChange(send.restorePackaging, { onSuccess: saved });
  return (
    <div className="row">
      <span className="muted">{entry.label}</span>
      <button
        type="button"
        className="link-button row"
        style={{ gap: 4 }}
        disabled={restore.isPending}
        onClick={() => restore.mutate({ category, size: sizeOf(entry) })}
      >
        <RotateCcw size={14} /> Put back
      </button>
      <ErrorNote error={restore.error} />
    </div>
  );
}

/** Adding a size: what one is called, and how much is in it, in any unit the kind is counted in. */
function AddPack({ category }: { category: Category }) {
  const saved = useSaved();
  const units = COUNTED_WAYS[category]
    .filter((d) => d !== "pitch")
    .flatMap((d) => DISPLAY_UNITS[d]);
  const start = CATEGORY_DEFAULTS[category].displayUnit;
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState<UnitId>(units.includes(start) ? start : (units[0] as UnitId));
  const add = useChange(send.setPackaging, {
    onSuccess: (r) => {
      saved(r);
      setName("");
      setValue("");
    },
  });
  const ready = name.trim() !== "" && isDecimalString(value) && Number(value) > 0;
  return (
    <form
      className="stack packaging-add"
      style={{ gap: 6 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) add.mutate({ category, name: name.trim(), size: { value, unit } });
      }}
    >
      <span className="muted">Add a size</span>
      <div className="row">
        <input
          className="input"
          aria-label="What one is called"
          placeholder="bag"
          list="pack-terms"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="input num"
          inputMode="decimal"
          aria-label="How much is in one"
          placeholder="Size"
          value={value}
          onChange={(e) => setValue(e.target.value.trim())}
        />
        <select
          className="select"
          aria-label="Unit"
          value={unit}
          onChange={(e) => setUnit(e.target.value as UnitId)}
        >
          {units.map((u) => (
            <option key={u} value={u}>
              {unitLabel(u)}
            </option>
          ))}
        </select>
        <button type="submit" className="button" disabled={!ready || add.isPending}>
          <Plus size={16} /> Add
        </button>
      </div>
      <ErrorNote error={add.error} />
    </form>
  );
}
